import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { importsApi, type GaPreview, type DomainPreview } from '../../api/imports.js';
import { ErrorBox, Field } from '../../components/ui.js';
import { num } from '../../lib/format.js';

/**
 * 붙여넣어 넣는 화면 (쓰기).
 *
 * 원본: market_ga_insert.php  (GA 자료)
 *       domain_rank_check.php (도메인 목록)
 *
 * 원본은 '입력하기' 를 누르면 곧장 DB 에 들어갔다. 붙여넣기를 잘못해도
 * 되돌릴 방법이 없어서, 여기서는 **미리보기를 먼저 보고 나서** 넣게 했다.
 */

type Kind = 'ga' | 'domains';

const TEXT: Record<Kind, { title: string; desc: string; label: string; placeholder: string }> = {
  ga: {
    title: 'GA 자료 입력',
    desc:
      'GA 내보내기 표를 그대로 붙여넣으세요 (탭으로 나뉜 형태). ' +
      '같은 소스·날짜·시간대가 이미 있으면 덮어씁니다.',
    label: 'GA 표',
    placeholder:
      '세션 소스/매체\t날짜 + 시간\t사용자\t세션수\t...\n' +
      'google / cpc\t2026081513\t988\t1003\t...',
  },
  domains: {
    title: '도메인 랭킹 체크',
    desc: '도메인을 한 줄에 하나씩 붙여넣으면 이미 등록된 것을 알려주고 목록에 넣습니다.',
    label: '도메인 목록',
    placeholder: '한 줄에 하나씩\nexample.com\n한국.kr',
  },
};

export function ImportPage({ kind }: { kind: Kind }) {
  const t = TEXT[kind];
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState(
    `result_${new Date().toISOString().slice(0, 10)}`,
  );
  const [skipExisting, setSkipExisting] = useState(true);
  const [done, setDone] = useState<string | null>(null);

  // 두 화면이 다른 모양을 돌려준다. 아래에서 kind 로 갈라 쓴다.
  const preview = useMutation<GaPreview | DomainPreview>({
    mutationFn: () =>
      kind === 'ga' ? importsApi.previewGa(text) : importsApi.previewDomains(text),
    onSuccess: () => setDone(null),
  });

  const commit = useMutation<{ written: number; skippedExisting?: number }>({
    mutationFn: () =>
      kind === 'ga'
        ? importsApi.commitGa(text)
        : importsApi.commitDomains(text, fileName, skipExisting),
    onSuccess: (r) => {
      setDone(
        (r.skippedExisting ?? 0) > 0
          ? `${num(r.written)}건 넣었습니다. 이미 있던 ${num(r.skippedExisting)}건은 건너뛰었습니다.`
          : `${num(r.written)}건 넣었습니다.`,
      );
      preview.reset();
      setText('');
    },
  });

  const p = preview.data;
  const ga = kind === 'ga' ? (p as GaPreview | undefined) : undefined;
  const dom = kind === 'domains' ? (p as DomainPreview | undefined) : undefined;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            {t.title}
            <span className="badge badge--danger">쓰기</span>
          </h1>
          <p className="page-desc">{t.desc}</p>
        </div>
      </header>

      <form
        className="lookup-form"
        onSubmit={(e) => {
          e.preventDefault();
          preview.mutate();
        }}
      >
        <Field label={t.label}>
          <textarea
            className="input lookup-input"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              preview.reset();
              setDone(null);
            }}
            placeholder={t.placeholder}
            rows={10}
            spellCheck={false}
          />
        </Field>

        {kind === 'domains' && (
          <div className="lookup-range">
            <Field label="파일 이름표 (down_filename)">
              <input className="input" value={fileName} onChange={(e) => setFileName(e.target.value)} />
            </Field>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
              />
              이미 있는 도메인은 넣지 않기
            </label>
          </div>
        )}

        <div className="lookup-actions">
          <span className="lookup-count">
            {text.trim() === '' ? '자료를 붙여넣어 주세요' : '먼저 확인한 뒤 넣습니다'}
          </span>
          <button className="btn" type="submit" disabled={preview.isPending || text.trim() === ''}>
            {preview.isPending ? '확인 중…' : '확인하기'}
          </button>
        </div>
      </form>

      {done && <p className="import-done">{done}</p>}
      {preview.isError && <ErrorBox error={preview.error} />}
      {commit.isError && <ErrorBox error={commit.error} />}

      {p && (
        <>
          <div className="summary">
            {ga && (
              <>
                <div className="summary__item">
                  <span className="summary__label">읽은 줄</span>
                  <strong className="summary__value">{num(ga.rows.length)}</strong>
                </div>
                <div className="summary__item">
                  <span className="summary__label">새로 들어감</span>
                  <strong className="summary__value">{num(ga.inserting)}</strong>
                </div>
                <div className="summary__item">
                  <span className="summary__label">덮어쓰기</span>
                  <strong className="summary__value">{num(ga.updating)}</strong>
                </div>
              </>
            )}
            {dom && (
              <>
                <div className="summary__item">
                  <span className="summary__label">읽은 도메인</span>
                  <strong className="summary__value">{num(dom.rows.length)}</strong>
                </div>
                <div className="summary__item">
                  <span className="summary__label">새 도메인</span>
                  <strong className="summary__value">{num(dom.fresh)}</strong>
                </div>
                <div className="summary__item">
                  <span className="summary__label">이미 있음</span>
                  <strong className="summary__value">{num(dom.duplicates)}</strong>
                </div>
              </>
            )}
            <div className="summary__item">
              <span className="summary__label">못 읽은 줄</span>
              <strong className="summary__value">{num(p.skipped.length)}</strong>
            </div>
          </div>

          {p.skipped.length > 0 && (
            <div className="lookup-missing">
              <strong>못 읽은 줄 {p.skipped.length}개 — 이 줄은 넣지 않습니다</strong>
              <textarea
                className="input"
                readOnly
                rows={Math.min(6, p.skipped.length + 1)}
                value={p.skipped.map((s) => `${s.line}행: ${s.why} — ${s.text}`).join('\n')}
              />
            </div>
          )}

          <div className="panel">
            <h2 className="panel__title">들어갈 내용 (앞 50줄)</h2>
            <div className="table-wrap">
              <table className="table">
                {ga ? (
                  <>
                    <thead>
                      <tr>
                        <th>소스/매체</th>
                        <th>날짜</th>
                        <th style={{ textAlign: 'right' }}>시간대</th>
                        <th style={{ textAlign: 'right' }}>세션수</th>
                        <th style={{ textAlign: 'right' }}>전환</th>
                        <th style={{ textAlign: 'right' }}>수익</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ga.rows.slice(0, 50).map((r, i) => (
                        <tr key={`${r.sess}-${r.ldate}-${r.ltime}-${i}`}>
                          <td>{r.sess}</td>
                          <td>{r.ldate}</td>
                          <td style={{ textAlign: 'right' }}>{r.ltime}</td>
                          <td style={{ textAlign: 'right' }}>{num(r.visit)}</td>
                          <td style={{ textAlign: 'right' }}>{num(r.ord)}</td>
                          <td style={{ textAlign: 'right' }}>{num(r.sale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead>
                      <tr>
                        <th>도메인</th>
                        <th>한글 도메인</th>
                        <th style={{ textAlign: 'center' }}>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dom!.rows.slice(0, 50).map((r) => (
                        <tr key={r.domainUs}>
                          <td className="token-cell">{r.domainUs}</td>
                          <td className="td-muted">{r.domainKr || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            {r.duplicate ? (
                              <span className="badge badge--warn">이미 있음</span>
                            ) : (
                              <span className="td-muted">새 도메인</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </table>
            </div>

            <div className="lookup-actions">
              <span className="lookup-count">
                확인하셨으면 아래 단추로 실제로 넣습니다. 되돌릴 수 없습니다.
              </span>
              <button
                className="btn"
                type="button"
                onClick={() => commit.mutate()}
                disabled={commit.isPending}
              >
                {commit.isPending ? '넣는 중…' : '넣기'}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
